import { useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "./useAuth";
import { useStore } from "@/lib/store";
import { apiRequest } from "@/lib/queryClient";

interface AgencyClient {
  id: string;
  name: string;
  userId: string | null;
  businessType: string;
  email: string | null;
  offerSheetId?: string | null;
  hasMetaIntegration: boolean;
  hasGhlIntegration?: boolean;
  hasGoogleAdsIntegration?: boolean;
  adAccounts?: { id: string; name: string; platform: string }[];
}

interface MyClientsResponse {
  clients: AgencyClient[];
}

const AGENCY_ROLES = new Set(["employee", "manager", "agency_owner", "super_admin"]);

function isAgencyRole(role: string | undefined): boolean {
  return AGENCY_ROLES.has(role ?? "");
}

export function useAgencyClients() {
  const { user } = useAuth();
  const { selectedClientId, selectedClientUserId, selectedClientName, setSelectedClient } =
    useStore();

  const userRole = user?.role;
  const enabled = !!user && isAgencyRole(userRole);

  const { data, isLoading } = useQuery<MyClientsResponse>({
    queryKey: ["my-clients"],
    queryFn: () => apiRequest("GET", "/api/my-clients"),
    enabled,
    staleTime: 60_000,
    // Retry transient backend failures (Supabase pooler timeouts on cold start)
    // so the sidebar selector populates without forcing the user to refresh.
    retry: (failureCount, err) => {
      if (err instanceof Error && err.message.startsWith("401:")) return false;
      return failureCount < 2;
    },
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 4000),
  });

  const clients = useMemo(() => data?.clients ?? [], [data?.clients]);
  const selectedClient = clients.find((c) => c.id === selectedClientId) ?? null;

  // Solo / Lead Gen UX: when the user has exactly one client and nothing is selected
  // yet, auto-select it so downstream flows (campaign creator, ad-graphics defaults,
  // FastTrackFlow.clientId) pick it up without manual sidebar interaction.
  useEffect(() => {
    if (!enabled) return;
    if (clients.length === 1 && !selectedClientId) {
      const only = clients[0];
      setSelectedClient({ id: only.id, userId: only.userId, name: only.name });
    }
  }, [clients, selectedClientId, setSelectedClient, enabled]);

  // The persisted selection can outlive changes to the linked client account.
  // Refresh its userId from the authoritative /api/my-clients response so
  // tenant-scoped screens never keep routing through a stale account.
  useEffect(() => {
    if (!selectedClient) return;
    if (
      selectedClientUserId !== selectedClient.userId ||
      selectedClientName !== selectedClient.name
    ) {
      setSelectedClient({
        id: selectedClient.id,
        userId: selectedClient.userId,
        name: selectedClient.name,
      });
    }
  }, [selectedClient, selectedClientName, selectedClientUserId, setSelectedClient]);

  return {
    clients,
    isLoading,
    isAgencyUser: enabled,
    selectedClientId,
    selectedClientUserId,
    selectedClientName,
    selectedClient,
    setSelectedClient,
  };
}
